import type { RazorpayOptions, RazorpayPaymentResponse, RazorpayError } from '../types/razorpay.d';

export interface RazorpayOrderData {
  orderId: string;
  amount: number;       // smallest unit (cents for USD)
  currency: string;
  keyId: string;
  tierId: string;
  billingCycle: string;
  development?: boolean;
}

export interface OpenCheckoutOptions {
  orderData: RazorpayOrderData;
  tierName: string;
  prefill?: { name?: string; email?: string };
  onSuccess: (response: RazorpayPaymentResponse) => void;
  onFailure: (error: string) => void;
}

/**
 * Opens the Razorpay hosted checkout modal.
 * Requires checkout.js to be loaded in index.html.
 *
 * For international (US/EU) cards: the Razorpay dashboard must have
 * "International Payments" enabled under Settings → Payment Methods.
 */
export function openRazorpayCheckout({
  orderData,
  tierName,
  prefill = {},
  onSuccess,
  onFailure,
}: OpenCheckoutOptions): void {
  if (typeof window.Razorpay === 'undefined') {
    onFailure('Razorpay checkout is unavailable. Please check your network connection.');
    return;
  }

  const billingLabel = orderData.billingCycle === 'annual' ? 'Annual' : 'Monthly';

  const options: RazorpayOptions = {
    key: orderData.keyId,
    amount: orderData.amount,
    currency: orderData.currency,
    name: 'BaaStream',
    description: `${tierName} Plan – ${billingLabel} subscription`,
    image: '/favicon.svg',
    order_id: orderData.orderId,
    handler: onSuccess,
    prefill: {
      name: prefill.name ?? '',
      email: prefill.email ?? '',
    },
    notes: {
      tierId: orderData.tierId,
      billingCycle: orderData.billingCycle,
    },
    theme: {
      color: '#FF7518', // BaaStream brand color
    },
    modal: {
      ondismiss: () => onFailure('Payment cancelled.'),
      escape: true,
    },
  };

  const rzp = new window.Razorpay(options);

  rzp.on('payment.failed', ({ error }: { error: RazorpayError }) => {
    onFailure(error.description || 'Payment failed. Please try a different card.');
  });

  rzp.open();
}
